/**
 * BusinessContextSection (FLOW-584)
 *
 * Read-only panel showing the project business context a work item was
 * written against — the current business priority ("strategic fit"), goals
 * and constraints — so a reader can see why a story exists without leaving
 * the item.
 *
 * Sits directly under `ContextStalenessBanner`, which is the "is this still
 * relevant?" prompt about this same context. The values are the project's
 * live ones, not per-item text, so there is no edit affordance here.
 */

import { Component, For, Show, createMemo, createResource } from "solid-js";
import { getBusinessContext } from "../../api/workItemContext";
import type { WorkItemId } from "../../api/types";

const SOURCE_NOTE = "From the project's business context — updates live.";

export interface BusinessContextSectionProps {
  workItemId: WorkItemId;
}

const ContextList: Component<{
  testId: string;
  label: string;
  items: string[];
}> = (props) => (
  <div data-testid={props.testId}>
    <h4 class="text-xs font-medium text-ink-3 mb-1">{props.label}</h4>
    <ul class="list-disc pl-5 space-y-1 text-sm text-ink-2">
      <For each={props.items}>{(item) => <li>{item}</li>}</For>
    </ul>
  </div>
);

export const BusinessContextSection: Component<BusinessContextSectionProps> = (
  props,
) => {
  const [context] = createResource(() => props.workItemId, getBusinessContext);

  // Reading an errored resource throws, and the nearest ErrorBoundary is the
  // app root — a failed fetch for this optional panel must render nothing
  // rather than blank the whole detail page. Gating on state === "ready"
  // avoids ever reading context() while it is in the error state.
  const ready = () => (context.state === "ready" ? context() : undefined);

  const priority = createMemo(() => ready()?.business_priority?.trim() || "");
  const goals = createMemo(() => ready()?.goals ?? []);
  const constraints = createMemo(() => ready()?.constraints ?? []);

  // `seeded` marks the items the planning/breakdown flow created, which is
  // the epic's stated scope; a card of three empty lists is noise on every
  // hand-made item.
  const visible = createMemo(
    () =>
      ready()?.seeded === true &&
      (priority() !== "" || goals().length > 0 || constraints().length > 0),
  );

  return (
    <Show when={visible()}>
      <section
        data-testid="business-context-section"
        aria-labelledby="business-context-heading"
        class="bg-surface rounded border border-line p-4"
      >
        <h3
          id="business-context-heading"
          class="text-sm font-medium text-ink-2"
        >
          Business context
        </h3>
        <p class="text-xs text-ink-3 mb-3">{SOURCE_NOTE}</p>

        <div class="space-y-3">
          <Show when={priority()}>
            {(text) => (
              <div data-testid="business-context-priority">
                <h4 class="text-xs font-medium text-ink-3 mb-1">
                  Strategic fit
                </h4>
                <p class="text-sm text-ink-2">{text()}</p>
              </div>
            )}
          </Show>

          <Show when={goals().length > 0}>
            <ContextList
              testId="business-context-goals"
              label="Goals"
              items={goals()}
            />
          </Show>

          <Show when={constraints().length > 0}>
            <ContextList
              testId="business-context-constraints"
              label="Constraints"
              items={constraints()}
            />
          </Show>
        </div>
      </section>
    </Show>
  );
};
